import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Package, Compass, Headphones, Sparkles, Box, Layers, CreditCard } from "lucide-react";
import { t } from "@/i18n/sk";
import { ASSETS } from "@/lib/assets";
import { getProducts, getExpansions, resolveImg } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ProductCard } from "@/components/ProductCard";
import { CategoryTile } from "@/components/CategoryTile";
import { HoloBackground } from "@/components/HoloBackground";

const trustIcons = [ShieldCheck, Package, Compass, Headphones];

function SectionHeader({ eyebrow, title, subtitle, action }) {
  return (
    <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
      <div>
        {eyebrow && <span className="overline mb-3 block">{eyebrow}</span>}
        <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">{title}</h2>
        {subtitle && <p className="mt-3 max-w-xl text-slate-400">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export default function Home() {
  const { settings } = useSettings();
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [expansions, setExpansions] = useState([]);
  const heroBg = resolveImg(settings?.hero_bg_url) || ASSETS.heroBg;
  const heroProduct = resolveImg(settings?.hero_image_url) || ASSETS.heroProduct;

  useEffect(() => {
    getProducts({ is_featured: true }).then(setFeatured).catch(() => {});
    getProducts({ is_new: true }).then(setNewArrivals).catch(() => {});
    getExpansions().then(setExpansions).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroBg} alt="" className="h-full w-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-b from-vault-black/50 via-vault-midnight/70 to-vault-black" />
          <div className="absolute inset-0 bg-gradient-to-r from-vault-black/90 via-transparent to-transparent" />
        </div>
        <HoloBackground />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-24 sm:px-6 lg:grid-cols-12 lg:py-32 lg:px-8">
          <div className="lg:col-span-7">
            <span className="overline mb-5 inline-flex items-center gap-2 rounded-full border border-violet-glow/30 bg-vault-black/50 px-4 py-1.5 backdrop-blur animate-fade-up">
              <Sparkles className="h-3.5 w-3.5" /> {t.hero.eyebrow}
            </span>
            <h1 className="font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white animate-fade-up sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>
              Každý balíček ukrýva <span className="holo-text animate-holo-shift">niečo vzácne.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 animate-fade-up sm:text-lg" style={{ animationDelay: "160ms" }}>{t.hero.sub}</p>
            <div className="mt-9 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "240ms" }}>
              <Link to="/serie" data-testid="hero-explore-collection-button" className="btn-glow inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-7 py-3.5 text-sm font-semibold text-white">
                {t.actions.explore} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/novinky" data-testid="hero-view-news-button" className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-vault-black/40 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur transition-all hover:border-cyan-glow/50 hover:bg-vault-black/60">
                {t.actions.viewNews}
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-400 animate-fade-up" style={{ animationDelay: "320ms" }}>
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-cyan-300" /> 100% autentické</span>
              <span className="flex items-center gap-2"><Package className="h-4 w-4 text-cyan-300" /> Starostlivé balenie</span>
              <span className="flex items-center gap-2"><Compass className="h-4 w-4 text-cyan-300" /> Nové série</span>
            </div>
          </div>
          <div className="hidden lg:col-span-5 lg:block">
            <div className="holo-ring group relative mx-auto flex aspect-square max-w-sm items-center justify-center overflow-hidden rounded-3xl border border-white/10 bg-vault-black/50 p-6 animate-fade-up" style={{ animationDelay: "260ms" }}>
              <span className="holo-sweep" />
              <img src={heroProduct} alt="Pokémon TCG produkt" className="h-full w-full rounded-2xl object-cover animate-float-slow" />
            </div>
          </div>
        </div>
      </section>

      <div className="vault-bg">
        {/* CATEGORIES */}
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Kategórie" title={t.categories.title} subtitle={t.categories.subtitle} />
          <div className="grid gap-5 md:grid-cols-3">
            <CategoryTile index={0} testId="category-tile-etb" to="/etb-boxy" title={t.nav.etb} description="Elite Trainer Boxy naprieč všetkými sériami." badge="Elite Trainer Box" image={ASSETS.catEtb} />
            <CategoryTile index={1} testId="category-tile-boosters" to="/boostre" title={t.nav.boosters} description="Booster packy aj booster boxy na otváranie." badge="Booster box & pack" image={ASSETS.catBoosters} />
            <CategoryTile index={2} testId="category-tile-cards" to="/karty" title={t.nav.cards} description="Jednotlivé zberateľské Pokémon TCG karty." badge="Singles" image={ASSETS.catCards} />
          </div>
        </section>

        {/* SERIES HIGHLIGHT */}
        {expansions.length > 0 && (
          <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow={t.seriesHighlight.eyebrow}
              title={t.seriesHighlight.title}
              subtitle={t.seriesHighlight.subtitle}
              action={<Link to="/serie" data-testid="series-view-all" className="hidden items-center gap-1 text-sm font-semibold text-cyan-300 hover:text-cyan-bright sm:inline-flex">{t.actions.viewAll} <ArrowRight className="h-4 w-4" /></Link>}
            />
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {expansions.slice(0, 8).map((exp, i) => (
                <Link key={exp.id} to={`/serie/${exp.slug}`} data-testid={`home-series-${exp.slug}`} className="group relative flex h-36 flex-col justify-end overflow-hidden rounded-xl border border-white/8 bg-vault-card p-4 transition-all duration-300 hover:border-violet-glow/50 animate-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
                  <div className="absolute inset-0">
                    <img src={resolveImg(exp.image_url)} alt={exp.name} loading="lazy" className="h-full w-full object-cover opacity-30 transition-all duration-500 group-hover:scale-105 group-hover:opacity-45" />
                    <div className="absolute inset-0 bg-gradient-to-t from-vault-black via-vault-black/80 to-transparent" />
                  </div>
                  <div className="relative z-10">
                    <div className="text-[0.6rem] uppercase tracking-wider text-cyan-300">{exp.release_label}</div>
                    <div className="font-display text-sm font-bold text-white">{exp.name}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* FEATURED */}
        {featured.length > 0 && (
          <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
            <SectionHeader eyebrow={t.featured.eyebrow} title={t.featured.title} subtitle={t.featured.subtitle} />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {featured.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </section>
        )}

        {/* NEW ARRIVALS */}
        {newArrivals.length > 0 && (
          <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow={t.newArrivals.eyebrow}
              title={t.newArrivals.title}
              subtitle={t.newArrivals.subtitle}
              action={<Link to="/novinky" data-testid="news-view-all" className="hidden items-center gap-1 text-sm font-semibold text-cyan-300 hover:text-cyan-bright sm:inline-flex">{t.actions.viewAll} <ArrowRight className="h-4 w-4" /></Link>}
            />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {newArrivals.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </section>
        )}

        {/* BRAND INTRO */}
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-white/8 bg-vault-card p-8 sm:p-12">
            <HoloBackground />
            <div className="relative z-10 max-w-3xl">
              <span className="overline mb-3 block">{t.brandIntro.eyebrow}</span>
              <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">{t.brandIntro.title}</h2>
              <p className="mt-4 leading-relaxed text-slate-300">{t.brandIntro.body}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/etb-boxy" className="inline-flex items-center gap-2 rounded-full border border-white/12 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-violet-glow/50"><Box className="h-4 w-4 text-violet-300" /> {t.nav.etb}</Link>
                <Link to="/boostre" className="inline-flex items-center gap-2 rounded-full border border-white/12 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-violet-glow/50"><Layers className="h-4 w-4 text-violet-300" /> {t.nav.boosters}</Link>
                <Link to="/karty" className="inline-flex items-center gap-2 rounded-full border border-white/12 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-violet-glow/50"><CreditCard className="h-4 w-4 text-violet-300" /> {t.nav.cards}</Link>
              </div>
            </div>
          </div>
        </section>

        {/* TRUST */}
        <section className="mx-auto max-w-7xl px-4 pb-20 pt-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Dôvera" title={t.trust.title} />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {t.trust.items.map((item, i) => {
              const Icon = trustIcons[i];
              return (
                <div key={i} className="group relative overflow-hidden rounded-2xl border border-white/8 bg-vault-card p-6 transition-colors hover:border-violet-glow/40">
                  <span className="holo-sweep" />
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-violet-glow/20 to-cyan-glow/20 text-cyan-300"><Icon className="h-6 w-6" /></div>
                  <h3 className="mb-2 text-base font-bold text-white">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-400">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
